import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver-es';
import { UnitsGridFormattingHelper } from '../../helpers/units-grid-formatting.helper';
import { UnitsGridColumn } from './units-grid-input.models';

export interface UnitsGridNumberFormatPreference {
  readonly thousands: string;
  readonly decimal: string;
  readonly [key: string]: unknown;
}

export interface UnitsGridPasteFormatResolutionArgs {
  readonly text: string;
  readonly hasNumericColumns: boolean;
  readonly enableDelimiterDialog: boolean;
  readonly currentThousandsDelimiter: string;
  readonly currentDecimalDelimiter: string;
  readonly dialogData: Readonly<Record<string, unknown>>;
  readonly openNumberFormatDialog: () => Promise<UnitsGridNumberFormatPreference | null>;
}

export interface UnitsGridExportToExcelArgs {
  readonly tableTitle: string;
  readonly columns: readonly UnitsGridColumn[];
  readonly rows: readonly Record<string, unknown>[];
  readonly getHeader: (column: UnitsGridColumn) => string;
  readonly saveAsFn?: typeof saveAs;
}

export function createUnitsGridFileInput(onChange: (event: Event) => Promise<void>): HTMLInputElement {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.csv,.txt';
  input.onchange = async (event) => {
    await onChange(event);
  };

  return input;
}

export async function readUnitsGridImportText(file: File): Promise<string> {
  const content = await file.text();
  return UnitsGridFormattingHelper.normalizeImportedText(content);
}

export async function resolveUnitsGridPasteFormat(args: UnitsGridPasteFormatResolutionArgs): Promise<{
  readonly text: string;
  readonly thousands: string;
  readonly decimal: string;
  readonly dialogResult?: UnitsGridNumberFormatPreference;
} | null> {
  if (!args.enableDelimiterDialog || !args.hasNumericColumns) {
    return {
      text: args.text,
      thousands: args.currentThousandsDelimiter,
      decimal: args.currentDecimalDelimiter
    };
  }

  const result = await args.openNumberFormatDialog();
  if (!result) {
    return null;
  }

  const normalizedPreference = {
    thousands: result.thousands ?? ',',
    decimal: result.decimal ?? '.'
  };

  return {
    text: args.text,
    thousands: normalizedPreference.thousands,
    decimal: normalizedPreference.decimal,
    dialogResult: result
  };
}

export async function exportUnitsGridToExcel(args: UnitsGridExportToExcelArgs): Promise<{
  readonly fileName: string;
  readonly rowCount: number;
}> {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(args.tableTitle);

  worksheet.columns = args.columns.map((column) => ({
    header: args.getHeader(column),
    key: column.field,
    width: 24
  }));

  args.rows.forEach((row) => {
    const rowValue: Record<string, unknown> = {};
    args.columns.forEach((column) => {
      rowValue[column.field] = row[column.field] ?? '';
    });
    worksheet.addRow(rowValue);
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const fileName = `${args.tableTitle.replace(/\s+/g, '_').toLowerCase()}_export.xlsx`;
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
  (args.saveAsFn ?? saveAs)(blob, fileName);

  return {
    fileName,
    rowCount: args.rows.length
  };
}
